import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  centsToDollarInputString,
  dollarsStringToCents,
  formatCentsAsCurrency,
} from '../Utils/money';
import { getContractorWorkers } from '../Config/contractorService';
import {
  fetchChargeTypesForOrderFlow,
  downloadOrderInvoicePdf,
  fetchOrderEligibleContractors,
  getErrorMessage,
  getNotificationTemplates,
  getOrderFinancialsMaster,
  patchApplyGuaranteeFee,
  patchOrderFlowCharge,
  postAcceptOrderFlowCharge,
  postAssignContractorToOrder,
  postAssignWorker,
  postCompleteOrder,
  postPaymentLinkSend,
  postPaymentRefund,
  postEndJob,
  postExtraCharge,
  postOnTheWay,
  postQuotation,
  postSendOrderNotification,
  postStartJob,
} from '../Config/adminOrderFlowService';
import {
  getSessionFlowSessions,
  unwrapSessionFlowList,
} from '../Config/adminSessionFlowService';
import AdminSessionFlowPanel from './AdminSessionFlowPanel';
import OrderFlowImageUploadList from './OrderFlowImageUploadList';

function inputClass(extra = '') {
  return `w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${extra}`;
}

function labelClass() {
  return 'block text-sm font-medium text-gray-700 mb-1';
}

function orderFlowChargeRowId(c) {
  if (c == null) return '';
  return c.charge_id ?? c.id ?? c.chargeId ?? '';
}

function orderFlowChargeEditable(c) {
  const s = String(c.status || '').toUpperCase();
  return s === 'PENDING' || s === 'ADMIN_APPROVED';
}

/** Non-base rows from financials master `charges.all` (editable amounts are a separate check). */
function orderFlowChargeIsExtra(c) {
  const t = String(c.charge_type || c.type || '').toUpperCase();
  if (t === 'BASE_SERVICE' || t === 'BASE') return false;
  return Boolean(orderFlowChargeRowId(c));
}

/**
 * Show Edit for extra charge amounts. Includes ACCEPTED (customer-approved) — invoice lines often omit charge_id,
 * so we drive the UI from `charges.all` instead of the line-items table alone.
 */
function orderFlowChargeAmountEditable(c) {
  if (!orderFlowChargeIsExtra(c)) return false;
  const s = String(c.status || '').toUpperCase();
  if (['DECLINED', 'REJECTED', 'CANCELED', 'CANCELLED', 'VOID'].includes(s)) return false;
  return ['PENDING', 'ADMIN_APPROVED', 'ACCEPTED'].includes(s);
}

/** Integer cents from API (*_cents / amount fields). */
function invoiceMoneyCents(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function humanizeChargeTypeLabel(raw) {
  const parts = String(raw || 'Extra')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase());
  return parts.length ? parts.join(' ') : 'Extra';
}

function financialsPaymentRows(master) {
  const p = master?.payment;
  if (!p || typeof p !== 'object') return [];
  const raw = p.database ?? p.payments ?? p.payment_intents ?? p.intents ?? [];
  return Array.isArray(raw) ? raw : [];
}

function paymentRowId(row) {
  if (row == null) return '';
  return row.payment_id ?? row.paymentId ?? row.id ?? row.pg_payment_id ?? '';
}

function paymentRowStatus(row) {
  return String(row?.status || '').toUpperCase();
}

function paymentRowAmountCents(row) {
  return invoiceMoneyCents(row?.amount_cents ?? row?.amount ?? 0);
}

function isOfflinePaymentRequiredMessage(message) {
  const value = String(message || '').toLowerCase();
  return [
    'no saved payment method',
    'no payment_method',
    'payment capture failed',
    'payment requires additional authentication',
    'order is short',
  ].some((fragment) => value.includes(fragment));
}

/** PI already captured/succeeded — not a real blocker when ledger shows nothing left to collect. */
function isStripeCaptureNotNeededMessage(message) {
  const value = String(message || '').toLowerCase();
  if (value.includes('requires_capture') && value.includes('succeeded')) return true;
  if (value.includes('already captured') || value.includes('already paid in full')) return true;
  return false;
}

function paymentRowHasCard(row) {
  if (!row || typeof row !== 'object') return false;
  const hints = row.stripe_hints ?? {};
  const card = hints.payment_method_details ?? row.payment_method_details ?? null;
  const pmId =
    row.payment_method_id ??
    row.payment_method ??
    hints.payment_method_id ??
    hints.payment_method ??
    null;
  if (pmId && String(pmId).trim()) return true;
  if (card && (card.brand || card.last4 || card.last_4)) return true;
  return false;
}

function resolveSavedCardStatus(master, errorMessage) {
  const msg = String(errorMessage || '').toLowerCase();
  if (msg.includes('no saved payment method') || msg.includes('no payment_method')) {
    return false;
  }
  const payment = master?.payment;
  if (payment && typeof payment === 'object') {
    if (typeof payment.has_payment_method === 'boolean') return payment.has_payment_method;
    if (typeof payment.has_saved_payment_method === 'boolean') return payment.has_saved_payment_method;
    if (typeof payment.has_card === 'boolean') return payment.has_card;
    const directPm =
      payment.payment_method_id ??
      payment.default_payment_method_id ??
      payment.saved_payment_method_id ??
      null;
    if (directPm && String(directPm).trim()) return true;
  }
  const rows = financialsPaymentRows(master);
  if (rows.some(paymentRowHasCard)) return true;
  return null;
}

function offlinePaymentFailureReason(errorMessage, hasCard) {
  const msg = String(errorMessage || '').toLowerCase();
  if (hasCard === false || msg.includes('no saved payment method') || msg.includes('no payment_method')) {
    return 'No card is saved on this order, so Stripe could not collect the remaining balance.';
  }
  if (msg.includes('payment requires additional authentication')) {
    return 'A card is on file, but the charge needs additional authentication (for example 3D Secure), so capture could not finish.';
  }
  if (msg.includes('payment capture failed')) {
    return 'A card is on file, but Stripe failed while capturing the remaining amount from that card.';
  }
  if (msg.includes('order is short')) {
    return 'Successful payments so far are less than the invoice total, so the order cannot be completed yet.';
  }
  if (hasCard === true) {
    return 'A card appears to be on file, but online collection of the remaining amount failed.';
  }
  return 'Online payment could not be collected for the remaining balance.';
}

function isRefundablePaymentRow(row) {
  const id = String(paymentRowId(row));
  return paymentRowStatus(row) === 'SUCCEEDED' && id.startsWith('pi_');
}

const REFUND_REASONS = [
  { value: 'requested_by_customer', label: 'Requested by customer' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'fraudulent', label: 'Fraudulent' },
];

function PaymentRefundRow({ row, orderCurrency, disabled, onRefunded }) {
  const paymentId = String(paymentRowId(row));
  const [open, setOpen] = useState(false);
  const [partial, setPartial] = useState(false);
  const [amountDollars, setAmountDollars] = useState('');
  const [reason, setReason] = useState('requested_by_customer');
  const [busy, setBusy] = useState(false);

  const maxCents = paymentRowAmountCents(row);

  const submit = async () => {
    if (!paymentId || disabled) return;
    const payload = { reason };
    if (partial) {
      const cents = dollarsStringToCents(amountDollars);
      if (Number.isNaN(cents) || cents <= 0) {
        toast.error('Enter a valid partial refund amount');
        return;
      }
      if (cents > maxCents) {
        toast.error('Refund amount exceeds this payment');
        return;
      }
      payload.amount_cents = cents;
    }
    setBusy(true);
    try {
      const { ok, body } = await postPaymentRefund(paymentId, payload);
      if (ok && body.success !== false) {
        const data = body.data ?? body;
        const refunded = data.amount_cents ?? payload.amount_cents ?? maxCents;
        toast.success(
          `Refund ${formatCentsAsCurrency(invoiceMoneyCents(refunded), orderCurrency)} submitted`
        );
        setOpen(false);
        setPartial(false);
        setAmountDollars('');
        if (onRefunded) await onRefunded();
      } else {
        toast.error(getErrorMessage(body, 'Refund failed'));
      }
    } catch {
      toast.error('Refund failed');
    } finally {
      setBusy(false);
    }
  };

  if (!isRefundablePaymentRow(row)) return null;

  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      {!open ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-medium text-rose-800 hover:bg-rose-50 disabled:opacity-50"
        >
          Refund
        </button>
      ) : (
        <div className="space-y-2 rounded-md border border-rose-200 bg-rose-50/50 p-2">
          <p className="text-xs font-medium text-rose-950">Refund {paymentId}</p>
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={partial}
              onChange={(e) => setPartial(e.target.checked)}
              disabled={disabled || busy}
            />
            Partial refund (default: full {formatCentsAsCurrency(maxCents, orderCurrency)})
          </label>
          {partial ? (
            <input
              type="number"
              step="0.01"
              min="0"
              className={inputClass()}
              placeholder="Amount in dollars"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              disabled={disabled || busy}
            />
          ) : null}
          <div>
            <label className={labelClass()}>Reason</label>
            <select
              className={inputClass()}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={disabled || busy}
            >
              {REFUND_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled || busy}
              onClick={submit}
              className="rounded-md bg-rose-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-800 disabled:opacity-50"
            >
              {busy ? 'Refunding…' : 'Confirm refund'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setPartial(false);
                setAmountDollars('');
              }}
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChargeEditRow({ charge, orderId, orderCurrency, disabled, onSaved, orderStatus }) {
  const id = orderFlowChargeRowId(charge);
  const cents = charge.amount_cents ?? charge.amount ?? 0;
  const [amount, setAmount] = useState(() => centsToDollarInputString(cents));
  const [remark, setRemark] = useState(charge.remark || '');
  const [adminRemark, setAdminRemark] = useState(charge.admin_remark || '');
  const [acceptUserRemark, setAcceptUserRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const orderTerminal = ['COMPLETE', 'CANCELED'].includes(String(orderStatus || '').toUpperCase());
  const canAcceptCharge =
    orderFlowChargeEditable(charge) && !orderTerminal && !disabled;

  useEffect(() => {
    setAmount(centsToDollarInputString(charge.amount_cents ?? charge.amount ?? 0));
    setRemark(charge.remark || '');
    setAdminRemark(charge.admin_remark || '');
  }, [charge]);

  const save = async () => {
    if (!id || disabled) return;
    const patch = {};
    const newCents = dollarsStringToCents(amount);
    if (Number.isNaN(newCents) || newCents < 0) {
      toast.error('Enter a valid amount in dollars');
      return;
    }
    const orig = Number(charge.amount_cents ?? charge.amount ?? 0);
    if (newCents !== orig) patch.amount = newCents;
    if ((remark || '') !== (charge.remark || '')) patch.remark = remark || undefined;
    if ((adminRemark || '') !== (charge.admin_remark || '')) patch.admin_remark = adminRemark || undefined;
    if (Object.keys(patch).length === 0) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const { ok, body } = await patchOrderFlowCharge(orderId, id, patch);
      if (ok && body.success !== false) {
        toast.success('Charge updated');
        if (onSaved) await onSaved();
      } else {
        toast.error(getErrorMessage(body, 'Update failed'));
      }
    } catch {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const acceptForCustomer = async () => {
    if (!id || !canAcceptCharge) return;
    setAccepting(true);
    try {
      const payload = acceptUserRemark.trim() ? { user_remark: acceptUserRemark.trim() } : {};
      const { ok, body } = await postAcceptOrderFlowCharge(orderId, id, payload);
      if (ok && body.success !== false) {
        toast.success('Charge accepted for customer');
        setAcceptUserRemark('');
        if (onSaved) await onSaved();
      } else {
        toast.error(getErrorMessage(body, 'Accept failed'));
      }
    } catch {
      toast.error('Accept failed');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 text-left">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-700">
          {charge.charge_type || charge.type || 'Charge'} · {charge.status}
        </span>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            disabled={disabled || saving}
            onClick={save}
            className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? '…' : 'Save'}
          </button>
          {canAcceptCharge ? (
            <button
              type="button"
              disabled={accepting}
              onClick={acceptForCustomer}
              className="rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {accepting ? '…' : 'Accept for customer'}
            </button>
          ) : null}
        </div>
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <div>
          <label className={labelClass()}>Amount ({orderCurrency})</label>
          <input
            type="number"
            step="0.01"
            min="0"
            className={inputClass()}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass()}>Remark</label>
          <input
            className={inputClass()}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="sm:col-span-3">
          <label className={labelClass()}>Admin remark</label>
          <input
            className={inputClass()}
            value={adminRemark}
            onChange={(e) => setAdminRemark(e.target.value)}
            disabled={disabled}
          />
        </div>
        {canAcceptCharge ? (
          <div className="sm:col-span-3 rounded-md border border-emerald-200/80 bg-emerald-50/40 p-2">
            <label className={labelClass()}>User-facing remark when accepting (optional)</label>
            <input
              className={inputClass()}
              value={acceptUserRemark}
              onChange={(e) => setAcceptUserRemark(e.target.value)}
              placeholder="Leave blank to use server default text"
              disabled={disabled || accepting}
            />
            <p className="mt-1 text-[11px] text-gray-600">
              The customer gets notified; totals may update the same as when they approve in the app.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function filterEligibleContractors(contractors, searchTerm) {
  const q = (searchTerm || '').toLowerCase().trim();
  if (!q) return contractors;
  return contractors.filter((c) => {
    const name = (c.user?.name || c.user?.business_name || '').toLowerCase();
    const email = (c.user?.email || '').toLowerCase();
    const mobile = String(c.user?.mobile || '');
    return name.includes(q) || email.includes(q) || mobile.includes(q);
  });
}

function filterWorkersForSearch(workers, searchTerm) {
  const q = (searchTerm || '').toLowerCase().trim();
  if (!q) return workers;
  return workers.filter((w) => {
    const name = (w.user?.name || '').toLowerCase();
    const email = (w.user?.email || '').toLowerCase();
    const mobile = String(w.user?.mobile || '');
    return name.includes(q) || email.includes(q) || mobile.includes(q);
  });
}

/** Prefer worker row id from API; fallback to `{ user_id }` per backend contract. */
function buildAssignWorkerBody(worker) {
  if (!worker) return {};
  const wid = worker.worker_id;
  const uid = worker.user?.user_id;
  if (wid) return { worker_id: wid };
  if (uid) return { user_id: uid };
  return {};
}

/** Same bases as admin quotation submit — direct start job blocked while PENDING quotation. */
const SERVICE_TYPES_WITH_QUOTATION_SUBMIT = new Set(['QUOTATION', 'DIAGNOSTIC', 'AUTO']);

function serviceAllowsQuotationSubmit(serviceType) {
  return SERVICE_TYPES_WITH_QUOTATION_SUBMIT.has(serviceType || '');
}

function hasPendingQuotationRows(quotation) {
  const rows = Array.isArray(quotation) ? quotation : quotation ? [quotation] : [];
  return rows.some((q) => String(q?.status || '').toUpperCase() === 'PENDING');
}

/** Strict sequence: ACCEPTED → on the way → ON_THE_WAY → start job → IN_PROGRESS → end job → DONE */
const JOB_LIFECYCLE_STEPS = [
  { id: 'on_the_way', label: 'Mark on the way', description: 'Worker is traveling to the customer.' },
  { id: 'start_job', label: 'Start job', description: 'Work has begun on site. Booked-but-not-started inspection visits on this order are cancelled automatically.' },
  { id: 'end_job', label: 'End job', description: 'Finish the job (optional minutes override).' },
];

function JobLifecyclePanel({
  orderStatus,
  orderContractorId,
  hasAssignedWorker,
  disabled,
  actionLoading,
  endJobMinutes,
  setEndJobMinutes,
  orderId,
  runAction,
  useInspectionSessionFlow,
  serviceType,
  quotation,
  loadFinancialsMaster,
}) {
  const [otwEtaMinutes, setOtwEtaMinutes] = useState('');
  const status = orderStatus || '';

  if (useInspectionSessionFlow && !['DONE', 'COMPLETE', 'CANCELED'].includes(status)) {
    return (
      <p className="text-sm text-gray-600">
        This order uses the <strong>order session flow</strong> APIs. Use <strong>Order session flow</strong> above for
        visits — not this legacy stepper.
      </p>
    );
  }

  if (['DONE', 'COMPLETE'].includes(status)) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
        <p className="font-medium">Job lifecycle complete</p>
        <p className="mt-1 text-emerald-800/90">Status is {status}. No further on-the-way / start / end actions apply.</p>
      </div>
    );
  }

  if (status === 'CANCELED') {
    return (
      <p className="text-sm text-gray-600">This order is canceled — job lifecycle actions are not available.</p>
    );
  }

  const quotationBlocksDirectStartJob =
    serviceAllowsQuotationSubmit(serviceType) && hasPendingQuotationRows(quotation);

  let stepIndex = -1;
  if (status === 'ACCEPTED' || status === 'INSPECTION') stepIndex = 0;
  else if (status === 'ON_THE_WAY') stepIndex = 1;
  else if (status === 'IN_PROGRESS') stepIndex = 2;
  else stepIndex = -1;

  if (stepIndex === -1) {
    if (!orderContractorId && ['PENDING', 'DRAFT'].includes(status)) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-medium">Assign a contractor first</p>
          <p className="mt-1 text-amber-900/90">
            Use <strong>Step 1 · Assign contractor</strong> above (or the order sidebar). Current status:{' '}
            <strong>{status || '—'}</strong>.
          </p>
        </div>
      );
    }
    if (orderContractorId && !hasAssignedWorker) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-medium">Assign a worker next</p>
          <p className="mt-1 text-amber-900/90">
            Use <strong>Step 2 · Assign worker</strong> above to pick someone from the assigned contractor&apos;s team.
            Current status: <strong>{status || '—'}</strong>.
          </p>
        </div>
      );
    }
    if (status === 'DRAFT') {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-medium">Order is DRAFT</p>
          <p className="mt-1 text-amber-900/90">
            Payment may still be pending or authorization did not succeed. Add a saved payment method where your process
            allows, or continue without card and fix payment from order details. Moves to PENDING when authorization
            succeeds (or amount is zero).
          </p>
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        <p className="font-medium">Next: mark on the way</p>
        <p className="mt-1 text-amber-900/90">
          The first lifecycle action is available when status is <strong>ACCEPTED</strong> or <strong>INSPECTION</strong>{' '}
          (outside the session-based inspection UI), after contractor acceptance and any quotation rules. Current status:{' '}
          <strong>{status || '—'}</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ol className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center text-sm text-gray-600">
        {JOB_LIFECYCLE_STEPS.map((step, i) => (
          <li key={step.id} className="flex items-center gap-2">
            <span
              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                i < stepIndex ? 'bg-emerald-600 text-white' : i === stepIndex ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {i < stepIndex ? '✓' : i + 1}
            </span>
            <span className={i === stepIndex ? 'font-semibold text-gray-900' : ''}>{step.label}</span>
            {i < JOB_LIFECYCLE_STEPS.length - 1 ? (
              <span className="hidden text-gray-300 sm:inline" aria-hidden>
                →
              </span>
            ) : null}
          </li>
        ))}
      </ol>

      {stepIndex === 0 && (
        <div>
          <p className="mb-3 text-sm text-gray-600">{JOB_LIFECYCLE_STEPS[0].description}</p>
          <div className="mb-3 max-w-xs">
            <label className={labelClass()}>ETA (minutes, optional)</label>
            <input
              type="number"
              min="0"
              className={inputClass()}
              placeholder="e.g. 20"
              value={otwEtaMinutes}
              onChange={(e) => setOtwEtaMinutes(e.target.value)}
              disabled={disabled}
            />
          </div>
          <button
            type="button"
            disabled={disabled || actionLoading === 'otw'}
            onClick={() => {
              const body = {};
              const n = Number(otwEtaMinutes);
              if (otwEtaMinutes !== '' && !Number.isNaN(n) && n >= 0) {
                body.eta_minutes = Math.round(n);
              }
              return runAction('otw', () => postOnTheWay(orderId, body));
            }}
            className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {actionLoading === 'otw' ? 'Updating…' : JOB_LIFECYCLE_STEPS[0].label}
          </button>
        </div>
      )}

      {stepIndex === 1 && (
        <div>
          <p className="mb-3 text-sm text-gray-600">{JOB_LIFECYCLE_STEPS[1].description}</p>
          {quotationBlocksDirectStartJob ? (
            <p className="mb-3 text-sm text-amber-900">
              Quotation-type service: resolve or accept the quotation before starting the job directly.
            </p>
          ) : null}
          <button
            type="button"
            disabled={
              disabled ||
              actionLoading === 'start' ||
              quotationBlocksDirectStartJob
            }
            onClick={() =>
              runAction('start', () => postStartJob(orderId, { cancel_pending_inspections: true }))
            }
            className="rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {actionLoading === 'start' ? 'Starting…' : JOB_LIFECYCLE_STEPS[1].label}
          </button>
        </div>
      )}

      {stepIndex === 2 && (
        <div>
          <p className="mb-3 text-sm text-gray-600">{JOB_LIFECYCLE_STEPS[2].description}</p>
          <label className={labelClass()}>Time taken (minutes, optional)</label>
          <div className="flex max-w-md flex-col gap-2 sm:flex-row sm:items-stretch">
            <input
              type="number"
              className={inputClass()}
              value={endJobMinutes}
              onChange={(e) => setEndJobMinutes(e.target.value)}
              min="0"
              placeholder="Override minutes"
              disabled={disabled}
            />
            <button
              type="button"
              disabled={disabled || actionLoading === 'end'}
              onClick={() =>
                runAction(
                  'end',
                  () => {
                    const body = {};
                    if (endJobMinutes !== '' && !Number.isNaN(Number(endJobMinutes))) {
                      body.timeTaken = Number(endJobMinutes);
                    }
                    return postEndJob(orderId, body);
                  },
                  loadFinancialsMaster
                )
              }
              className="whitespace-nowrap rounded-lg bg-purple-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-50"
            >
              {actionLoading === 'end' ? 'Ending…' : JOB_LIFECYCLE_STEPS[2].label}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OutreachPlaceholdersHint({ placeholders }) {
  if (placeholders == null) return null;
  if (Array.isArray(placeholders)) {
    if (placeholders.length === 0) return null;
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 text-left">
        <p className="text-xs font-semibold text-gray-700">Placeholders</p>
        <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-xs text-gray-600">
          {placeholders.map((p, i) => (
            <li key={i}>
              {typeof p === 'string'
                ? p
                : [p.key, p.name, p.placeholder].filter(Boolean).join(' ') || '—'}
              {typeof p === 'object' && p != null && (p.description || p.text || p.label) ? (
                <span className="text-gray-500"> — {p.description || p.text || p.label}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (typeof placeholders === 'object') {
    const entries = Object.entries(placeholders);
    if (entries.length === 0) return null;
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 text-left">
        <p className="text-xs font-semibold text-gray-700">Placeholders</p>
        <ul className="mt-1.5 space-y-0.5 text-xs text-gray-600">
          {entries.map(([k, v]) => (
            <li key={k}>
              <code className="rounded bg-white px-1 py-0.5 text-[11px]">{k}</code>
              <span className="text-gray-500"> — {String(v)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
}

function SubSection({ title, children }) {
  return (
    <details className="group rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer list-none px-4 py-3 font-semibold text-gray-900 sm:px-5 [&::-webkit-details-marker]:hidden">
        <span className="flex items-center justify-between gap-2">
          {title}
          <span className="text-gray-400 transition group-open:rotate-180">▼</span>
        </span>
      </summary>
      <div className="space-y-4 border-t border-gray-100 p-4 sm:p-5">{children}</div>
    </details>
  );
}

/**
 * Admin order-flow API shortcuts for a single order (same routes as /api/v1/admin/order-flow).
 */
function AdminOrderFlowActions({
  orderId,
  service,
  disabled,
  onRefresh,
  orderStatus,
  orderContractorId,
  orderWorker,
  customerUserId,
  orderNumericId,
  /** Server quotation rows (for guarding direct Start job). */
  orderQuotation,
  /** Customer preferred slot ISOs (order `time_options`) for inspection booking chips. */
  orderTimeOptions = [],
  /** ISO persisted by PATCH …/planned-job-visit (from order GET, e.g. `planned_job_visit_at`). */
  plannedJobVisitAt = null,
  onRecordOfflinePayment,
}) {
  const [quotation, setQuotation] = useState({
    amount: '',
    details: '',
    images: [],
    require_user_accept: true,
  });
  const [extraCharge, setExtraCharge] = useState({
    charge_type: '',
    amount: '',
    remark: '',
    images: [],
    require_user_accept: true,
  });
  const [quotationImageBusy, setQuotationImageBusy] = useState(false);
  const [extraChargeImageBusy, setExtraChargeImageBusy] = useState(false);
  const [extraChargeTypeCustom, setExtraChargeTypeCustom] = useState('');
  const [eligibleContractors, setEligibleContractors] = useState([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [contractorSearch, setContractorSearch] = useState('');
  const [contractorWorkers, setContractorWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [workerSearch, setWorkerSearch] = useState('');
  const [endJobMinutes, setEndJobMinutes] = useState('');
  const [financialsMaster, setFinancialsMaster] = useState(null);
  const [financialsMasterLoading, setFinancialsMasterLoading] = useState(false);
  const [financialsMasterError, setFinancialsMasterError] = useState(null);
  const [invoicePdfBusy, setInvoicePdfBusy] = useState(false);
  const [financialsEditingChargeId, setFinancialsEditingChargeId] = useState(null);
  const [paymentLinkSmsBusy, setPaymentLinkSmsBusy] = useState(false);
  const [completeForm, setCompleteForm] = useState({
    worker_remark: '',
  });
  const [offlinePaymentWarning, setOfflinePaymentWarning] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [chargeTypesFromApi, setChargeTypesFromApi] = useState(null);
  const [outreachTemplates, setOutreachTemplates] = useState([]);
  const [outreachTemplateKey, setOutreachTemplateKey] = useState('');
  const [outreachChannels, setOutreachChannels] = useState('both');
  const [outreachFcmTitle, setOutreachFcmTitle] = useState('');
  /** Single body for SMS + FCM when not using split copy (API `message`). */
  const [outreachMessage, setOutreachMessage] = useState('');
  /** Only when channel is both + split: separate bodies. */
  const [outreachFcmBody, setOutreachFcmBody] = useState('');
  const [outreachSmsBody, setOutreachSmsBody] = useState('');
  const [outreachUseSplitCopy, setOutreachUseSplitCopy] = useState(false);

  const [sessionFlowSessions, setSessionFlowSessions] = useState([]);
  const [sessionsBundleLoading, setSessionsBundleLoading] = useState(false);

  const serviceChargeTypeFallback = useMemo(() => {
    const raw = service?.valid_extra_charge;
    return Array.isArray(raw) ? raw : [];
  }, [service]);

  const chargeTypeSelectOptions = useMemo(() => {
    if (chargeTypesFromApi !== null && chargeTypesFromApi.length > 0) {
      return chargeTypesFromApi;
    }
    return serviceChargeTypeFallback;
  }, [chargeTypesFromApi, serviceChargeTypeFallback]);

  const serviceType = service?.service_type || '';
  const allowsQuotationSubmit = serviceAllowsQuotationSubmit(serviceType);
  const hasAssignedWorker = Boolean(orderWorker);
  const needsContractor = !orderContractorId;
  const needsWorker = Boolean(orderContractorId) && !hasAssignedWorker;

  useEffect(() => {
    if (!orderId || disabled || !needsContractor) return;
    let cancelled = false;
    setLoadingContractors(true);
    fetchOrderEligibleContractors(orderId)
      .then((list) => {
        if (!cancelled) setEligibleContractors(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setEligibleContractors([]);
          toast.error(err?.message || 'Failed to load contractors');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingContractors(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, disabled, needsContractor]);

  useEffect(() => {
    if (!orderId || disabled || !needsWorker || !orderContractorId) {
      setContractorWorkers([]);
      setWorkerSearch('');
      return;
    }
    let cancelled = false;
    setLoadingWorkers(true);
    getContractorWorkers(orderContractorId, 1, 200)
      .then((response) => {
        if (cancelled) return;
        if (response.status === 'success') {
          const raw = response.data;
          const list = Array.isArray(raw) ? raw : raw?.data || [];
          setContractorWorkers(Array.isArray(list) ? list : []);
        } else {
          setContractorWorkers([]);
          toast.error(response.message || 'Failed to load workers');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setContractorWorkers([]);
          toast.error('Failed to load workers');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingWorkers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, disabled, needsWorker, orderContractorId]);

  useEffect(() => {
    let cancelled = false;
    fetchChargeTypesForOrderFlow()
      .then((types) => {
        if (!cancelled) setChargeTypesFromApi(types);
      })
      .catch(() => {
        if (!cancelled) setChargeTypesFromApi([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getNotificationTemplates().then((res) => {
      if (cancelled || !res.ok) return;
      const templates = res.body?.data?.templates;
      if (Array.isArray(templates)) setOutreachTemplates(templates);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (outreachChannels !== 'both') {
      setOutreachUseSplitCopy(false);
    }
  }, [outreachChannels]);

  const orderCurrency = service?.currency || 'CAD';

  const loadFinancialsMaster = useCallback(async () => {
    if (!orderId) return;
    setFinancialsMasterLoading(true);
    setFinancialsMasterError(null);
    try {
      const { ok, body } = await getOrderFinancialsMaster(orderId);
      if (ok && body.success !== false) {
        setFinancialsMaster(body.data ?? body);
      } else {
        setFinancialsMaster(null);
        setFinancialsMasterError(getErrorMessage(body, 'Failed to load invoice preview'));
      }
    } catch (e) {
      setFinancialsMaster(null);
      setFinancialsMasterError(e?.message || 'Failed to load invoice preview');
    } finally {
      setFinancialsMasterLoading(false);
    }
  }, [orderId]);

  const loadSessionFlowBundle = useCallback(async () => {
    if (!orderId) return;
    setSessionsBundleLoading(true);
    try {
      const sessRes = await getSessionFlowSessions(orderId);
      const list = unwrapSessionFlowList(sessRes.body, ['sessions', 'data']);
      setSessionFlowSessions(Array.isArray(list) ? list : []);
    } catch {
      setSessionFlowSessions([]);
    } finally {
      setSessionsBundleLoading(false);
    }
  }, [orderId]);

  const refreshOrderAndSessions = useCallback(async () => {
    await loadSessionFlowBundle();
    if (onRefresh) await onRefresh();
  }, [loadSessionFlowBundle, onRefresh]);

  const refreshAllOrderFlowAfterMutation = useCallback(async () => {
    await Promise.all([loadFinancialsMaster(), loadSessionFlowBundle()]);
    if (onRefresh) await onRefresh();
  }, [loadFinancialsMaster, loadSessionFlowBundle, onRefresh]);

  useEffect(() => {
    loadFinancialsMaster();
  }, [loadFinancialsMaster]);

  useEffect(() => {
    loadSessionFlowBundle();
  }, [loadSessionFlowBundle]);

  useEffect(() => {
    setFinancialsEditingChargeId(null);
  }, [orderId]);

  const handleChargeEditorSaved = useCallback(async () => {
    setFinancialsEditingChargeId(null);
    await loadFinancialsMaster();
  }, [loadFinancialsMaster]);

  const runAction = async (key, fn, afterSuccess) => {
    if (!orderId || disabled) return;
    setActionLoading(key);
    try {
      const { ok, body } = await fn();
      if (ok && body.success !== false) {
        toast.success('Done');
        if (typeof afterSuccess === 'function') await afterSuccess();
        await refreshOrderAndSessions();
      } else {
        toast.error(getErrorMessage(body, 'Action failed'));
      }
    } catch {
      toast.error('Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendOutreach = async () => {
    if (!orderId || disabled) return;
    if (!customerUserId || orderNumericId == null || String(orderNumericId).trim() === '') {
      toast.error('Customer or order is missing for outreach.');
      return;
    }
    const ft = outreachFcmTitle.trim();
    const msg = outreachMessage.trim();
    const fb = outreachFcmBody.trim();
    const sb = outreachSmsBody.trim();
    const templateKey = outreachTemplateKey.trim();
    const useSplit = outreachChannels === 'both' && outreachUseSplitCopy;

    if (useSplit) {
      if (!fb && !sb) {
        toast.error('Enter the app message and/or SMS message, or turn off “different wording” and use one message.');
        return;
      }
    } else if (!msg) {
      toast.error('Enter a message.');
      return;
    }

    setActionLoading('outreach');
    try {
      const payload = {
        user_id: customerUserId,
        order_id: Number(orderNumericId),
        channels: outreachChannels,
      };
      if (templateKey) payload.template_key = templateKey;
      if (ft && (outreachChannels === 'fcm' || outreachChannels === 'both')) {
        payload.fcm_title = ft;
      }
      if (useSplit) {
        if (fb) payload.fcm_body = fb;
        if (sb) payload.sms_body = sb;
      } else if (msg) {
        payload.message = msg;
      }

      const { ok, body } = await postSendOrderNotification(payload);
      if (ok && body.success !== false) {
        const data = body.data && typeof body.data === 'object' ? body.data : {};
        const warn = data.warnings;
        if (Array.isArray(warn) && warn.length > 0) {
          toast.warning(`Sent with warnings: ${warn.join('; ')}`);
        } else if (warn != null && typeof warn === 'string' && warn.trim()) {
          toast.warning(warn);
        } else {
          toast.success('Notification sent');
        }
      } else {
        toast.error(getErrorMessage(body, 'Send failed'));
      }
    } catch {
      toast.error('Send failed');
    } finally {
      setActionLoading(null);
    }
  };

  const selectedOutreachTemplate = useMemo(
    () => outreachTemplates.find((t) => (t.id || t.template_key) === outreachTemplateKey) || null,
    [outreachTemplates, outreachTemplateKey]
  );

  const handleOutreachTemplateChange = (id) => {
    setOutreachTemplateKey(id);
    if (!id) {
      setOutreachFcmTitle('');
      setOutreachMessage('');
      setOutreachFcmBody('');
      setOutreachSmsBody('');
      setOutreachUseSplitCopy(false);
      return;
    }
    const t = outreachTemplates.find((x) => (x.id || x.template_key) === id);
    if (!t) return;
    const primary =
      typeof t.message === 'string'
        ? t.message
        : typeof t.fcm_body === 'string'
          ? t.fcm_body
          : '';
    const fcmb = typeof t.fcm_body === 'string' ? t.fcm_body : primary;
    const smsb = typeof t.sms_body === 'string' ? t.sms_body : primary;
    setOutreachFcmTitle(typeof t.fcm_title === 'string' ? t.fcm_title : '');
    setOutreachMessage(primary);
    setOutreachFcmBody(fcmb);
    setOutreachSmsBody(smsb);
    setOutreachUseSplitCopy(false);
  };

  const handleSendPaymentLinkSms = async () => {
    if (disabled) return;
    const numericId = Number(orderNumericId ?? orderId);
    if (!Number.isFinite(numericId)) {
      toast.error('Invalid order id');
      return;
    }
    setPaymentLinkSmsBusy(true);
    try {
      const { ok, body } = await postPaymentLinkSend(numericId);
      if (ok && body.success !== false) {
        toast.success(body.message || 'Payment link SMS queued');
        await loadFinancialsMaster();
      } else {
        toast.error(getErrorMessage(body, 'Could not queue payment link SMS'));
      }
    } catch {
      toast.error('Could not queue payment link SMS');
    } finally {
      setPaymentLinkSmsBusy(false);
    }
  };

  const handleInvoicePdf = async () => {
    if (!orderId) return;
    setInvoicePdfBusy(true);
    try {
      await downloadOrderInvoicePdf(orderId);
      toast.success('Invoice download started');
    } catch (e) {
      toast.error(e?.message || 'Could not download PDF');
    } finally {
      setInvoicePdfBusy(false);
    }
  };

  const handleCompleteOrder = async () => {
    if (!orderId || disabled) return;
    setActionLoading('complete');
    try {
      const payload = {
        mode: 'stripe_capture',
        worker_remark: completeForm.worker_remark || undefined,
      };
      const { ok, status, body } = await postCompleteOrder(orderId, payload);
      if (ok && body.success !== false) {
        const data = body.data ?? {};
        if (data.skippedCapture) {
          toast.success('Order completed — customer already paid in full; no card capture.');
        } else if (data.overpaidRefund) {
          toast.success('Order completed — excess payment was refunded to match the invoice.');
        } else if (data.paymentCaptured) {
          const cap = Number(data.captureAmount);
          const capMsg =
            Number.isFinite(cap) && cap > 0
              ? ` — captured ${formatCentsAsCurrency(Math.round(cap * 100), orderCurrency)}`
              : '';
          toast.success(`Order completed${capMsg}.`);
        } else {
          toast.success('Order completed');
        }
        await loadFinancialsMaster();
        await loadSessionFlowBundle();
        if (onRefresh) await onRefresh();
      } else {
        const message = getErrorMessage(body, 'Complete failed');
        if (status === 400 && isOfflinePaymentRequiredMessage(message)) {
          await loadFinancialsMaster();
          setOfflinePaymentWarning({ message });
        } else {
          toast.error(message);
        }
      }
    } catch {
      toast.error('Complete failed');
    } finally {
      setActionLoading(null);
    }
  };

  const orderStatusUpper = String(orderStatus || '').toUpperCase();
  /** Prefer unified session-flow APIs over legacy order-level on-the-way / start job */
  const sessionFlowReplacesLegacy =
    sessionFlowSessions.length > 0 ||
    ['INSPECTION', 'ON_THE_WAY', 'IN_PROGRESS'].includes(orderStatusUpper) ||
    (orderStatusUpper === 'ACCEPTED' && hasAssignedWorker);

  const showInspectionSection =
    !['DONE', 'COMPLETE', 'CANCELED'].includes(orderStatusUpper) &&
    (sessionsBundleLoading ||
      sessionFlowSessions.length > 0 ||
      ['INSPECTION', 'ON_THE_WAY', 'IN_PROGRESS'].includes(orderStatusUpper) ||
      (orderStatusUpper === 'ACCEPTED' && hasAssignedWorker));

  const guaranteeFeeApplied = useMemo(() => {
    const m = financialsMaster;
    if (!m || typeof m !== 'object') return true;
    const raw =
      m.meta?.apply_guarantee_fee ??
      m.meta?.applyGuaranteeFee ??
      m.order?.apply_guarantee_fee ??
      m.order?.applyGuaranteeFee ??
      m.apply_guarantee_fee ??
      m.applyGuaranteeFee;
    if (typeof raw === 'boolean') return raw;
    return true;
  }, [financialsMaster]);

  if (!orderId) return null;

  const canOutreach = Boolean(customerUserId) && orderNumericId != null && String(orderNumericId).trim() !== '';
  const showOutreachFcm = outreachChannels === 'fcm' || outreachChannels === 'both';
  const outreachSplitActive = outreachChannels === 'both' && outreachUseSplitCopy;
  const outreachHasAnyBody = outreachSplitActive
    ? Boolean(outreachFcmBody.trim() || outreachSmsBody.trim())
    : Boolean(outreachMessage.trim());
  const outreachSendDisabled =
    disabled || actionLoading === 'outreach' || !canOutreach || !outreachHasAnyBody;

  const co = financialsMaster?.completion_options;
  const stripeBlockers = co?.stripe_capture?.blocking_reasons;
  const masterReady = financialsMaster != null && !financialsMasterLoading;
  const lineItems = financialsMaster?.customer_invoice?.line_items ?? [];
  const totalDue = financialsMaster?.customer_invoice?.breakdown?.total_amount;
  const payCmp = financialsMaster?.payment?.comparison;
  const amountRemainingCents =
    payCmp != null && typeof payCmp.amount_remaining_cents === 'number'
      ? invoiceMoneyCents(payCmp.amount_remaining_cents)
      : null;
  const invoiceFullyPaid = amountRemainingCents != null && amountRemainingCents <= 0;
  const structuralStripeBlockers = Array.isArray(stripeBlockers)
    ? stripeBlockers.filter(
        (reason) =>
          !isOfflinePaymentRequiredMessage(reason) &&
          !(invoiceFullyPaid && isStripeCaptureNotNeededMessage(reason))
      )
    : [];
  const stripeCaptureSkippedNote =
    invoiceFullyPaid &&
    Array.isArray(stripeBlockers) &&
    stripeBlockers.some(isStripeCaptureNotNeededMessage);
  const canStripeComplete =
    masterReady && orderStatusUpper === 'DONE' && structuralStripeBlockers.length === 0;
  const canRecordOfflinePayment =
    !disabled &&
    typeof onRecordOfflinePayment === 'function' &&
    (amountRemainingCents == null || amountRemainingCents > 0);
  const amountTotalCents =
    payCmp != null
      ? invoiceMoneyCents(payCmp.amount_total_cents ?? payCmp.invoice_total_cents ?? totalDue)
      : totalDue != null
        ? invoiceMoneyCents(totalDue)
        : null;
  const amountReceivedCents =
    payCmp != null && typeof payCmp.amount_received_cents === 'number'
      ? invoiceMoneyCents(payCmp.amount_received_cents)
      : null;
  const warningHasCard = offlinePaymentWarning
    ? resolveSavedCardStatus(financialsMaster, offlinePaymentWarning.message)
    : null;
  const warningReason = offlinePaymentWarning
    ? offlinePaymentFailureReason(offlinePaymentWarning.message, warningHasCard)
    : '';
  const warningCardLabel =
    warningHasCard === true ? 'Yes — card / payment method is on file' : warningHasCard === false ? 'No — no card saved' : 'Unknown — could not confirm from invoice data';
  const warningPaymentMethodLabel =
    warningHasCard === true ? 'Attached' : warningHasCard === false ? 'Not attached' : 'Unknown';
  const paymentRows = financialsPaymentRows(financialsMaster);
  const pendingPreTax = financialsMaster?.charges?.pending_pre_tax_sum_cents;
  const allCharges = Array.isArray(financialsMaster?.charges?.all)
    ? financialsMaster.charges.all
    : [];
  const contractorLines = financialsMaster?.contractor_preview?.line_items ?? [];
  const moneyHint = financialsMaster?.meta?.display_money_hint;
  const invoiceCurrency = String(
    financialsMaster?.meta?.currency ?? service?.currency ?? 'CAD'
  ).toUpperCase();
  const extraChargesList = allCharges.filter(orderFlowChargeIsExtra);

  return (
    <div className="space-y-4">
      {disabled ? (
        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-800">
            Order is complete or canceled — shortcuts below are read-only. You can still download the customer invoice.
          </p>
          <button
            type="button"
            disabled={invoicePdfBusy}
            onClick={handleInvoicePdf}
            className="shrink-0 rounded-lg bg-slate-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
          >
            {invoicePdfBusy ? 'Preparing PDF…' : 'Download invoice PDF'}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600">
            Assign contractor and worker from lists, then{' '}
            {allowsQuotationSubmit ? 'quotation (quotation / diagnostic / auto services only), ' : ''}
            extra charges, customer SMS / app notifications; use <strong>Order session flow</strong> below once a
            contractor and worker are assigned (schedule → on the way → start → complete). Monetary amounts use
            dollars — converted to cents for the API.
          </p>
          {canRecordOfflinePayment ? (
            <button
              type="button"
              onClick={onRecordOfflinePayment}
              className="shrink-0 rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-800"
            >
              Record offline payment
            </button>
          ) : null}
        </div>
      )}

      <div className={disabled ? 'pointer-events-none opacity-50' : ''}>
      <SubSection title="Customer outreach (SMS & app)">
        {!canOutreach ? (
          <p className="text-sm text-gray-600">Customer user or order id is missing — outreach is not available.</p>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              Load a template, then edit the <strong>message</strong> in one place — replace{' '}
              <code className="rounded bg-gray-100 px-1 text-xs">{'{{userName}}'}</code>,{' '}
              <code className="rounded bg-gray-100 px-1 text-xs">{'{{orderId}}'}</code>,{' '}
              <code className="rounded bg-gray-100 px-1 text-xs">{'{{text}}'}</code> inline yourself, or leave tokens and
              the server fills what&apos;s still there. We send <code className="rounded bg-gray-100 px-1 text-xs">message</code>{' '}
              for both SMS and app unless you check &quot;different wording&quot;.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass()}>Channel</label>
                <select
                  className={inputClass()}
                  value={outreachChannels}
                  onChange={(e) => setOutreachChannels(e.target.value)}
                  disabled={disabled}
                >
                  <option value="both">SMS and app (FCM)</option>
                  <option value="sms">SMS only</option>
                  <option value="fcm">App (FCM) only</option>
                </select>
              </div>
              <div>
                <label className={labelClass()}>Template</label>
                <select
                  className={inputClass()}
                  value={outreachTemplateKey}
                  onChange={(e) => handleOutreachTemplateChange(e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Start empty (type your own)</option>
                  {outreachTemplates.map((t) => {
                    const tid = t.id || t.template_key || '';
                    return (
                      <option key={tid} value={tid} title={t.description || ''}>
                        {t.label || tid}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="sm:col-span-2">
                <OutreachPlaceholdersHint placeholders={selectedOutreachTemplate?.placeholders} />
              </div>
              {showOutreachFcm ? (
                <div className="sm:col-span-2">
                  <label className={labelClass()}>App title (push)</label>
                  <input
                    className={inputClass()}
                    value={outreachFcmTitle}
                    onChange={(e) => setOutreachFcmTitle(e.target.value)}
                    placeholder="e.g. Order update"
                    disabled={disabled}
                  />
                </div>
              ) : null}
              {outreachChannels === 'both' ? (
                <div className="sm:col-span-2">
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={outreachUseSplitCopy}
                      onChange={(e) => {
                        const on = e.target.checked;
                        if (on) {
                          const m = outreachMessage.trim();
                          setOutreachUseSplitCopy(true);
                          setOutreachFcmBody((b) => (b.trim() ? b : m));
                          setOutreachSmsBody((b) => (b.trim() ? b : m));
                        } else {
                          setOutreachUseSplitCopy(false);
                          setOutreachMessage(
                            outreachFcmBody.trim() || outreachSmsBody.trim() || outreachMessage
                          );
                        }
                      }}
                      disabled={disabled}
                    />
                    <span>
                      <span className="font-medium">Different wording for SMS and app</span>
                      <span className="block text-xs font-normal text-gray-500">
                        Off by default — same text for SMS and app. Turn on only when push and SMS need different copy.
                      </span>
                    </span>
                  </label>
                </div>
              ) : null}
              {!outreachSplitActive ? (
                <div className="sm:col-span-2">
                  <label className={labelClass()}>Message</label>
                  <textarea
                    className={inputClass()}
                    rows={6}
                    value={outreachMessage}
                    onChange={(e) => setOutreachMessage(e.target.value)}
                    placeholder={
                      outreachChannels === 'sms'
                        ? 'SMS to the customer…'
                        : outreachChannels === 'fcm'
                          ? 'In-app notification body…'
                          : 'SMS and in-app body (same text)…'
                    }
                    disabled={disabled}
                  />
                </div>
              ) : (
                <>
                  <div className="sm:col-span-2">
                    <label className={labelClass()}>App message (FCM)</label>
                    <textarea
                      className={inputClass()}
                      rows={4}
                      value={outreachFcmBody}
                      onChange={(e) => setOutreachFcmBody(e.target.value)}
                      placeholder="Push notification body…"
                      disabled={disabled}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass()}>SMS message</label>
                    <textarea
                      className={inputClass()}
                      rows={4}
                      value={outreachSmsBody}
                      onChange={(e) => setOutreachSmsBody(e.target.value)}
                      placeholder="SMS body…"
                      disabled={disabled}
                    />
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              disabled={outreachSendDisabled}
              onClick={handleSendOutreach}
              className="rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {actionLoading === 'outreach' ? 'Sending…' : 'Send notification'}
            </button>
          </>
        )}
      </SubSection>

      {!disabled && (needsContractor || needsWorker || hasAssignedWorker) ? (
        <div className="space-y-5 rounded-xl border border-indigo-200 bg-indigo-50/30 p-4 sm:p-5">
          {needsContractor ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Step 1 · Assign contractor</h3>
              <p className="mt-1 text-xs text-gray-600">
                Contractors who can serve this order (same list as the sidebar). Pick one to continue.
              </p>
              <input
                type="search"
                className={`${inputClass()} mt-3`}
                placeholder="Search by name, email, or mobile…"
                value={contractorSearch}
                onChange={(e) => setContractorSearch(e.target.value)}
                disabled={disabled}
              />
              <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                {loadingContractors ? (
                  <p className="p-4 text-sm text-gray-500">Loading contractors…</p>
                ) : filterEligibleContractors(eligibleContractors, contractorSearch).length === 0 ? (
                  <p className="p-4 text-sm text-gray-500">No contractors match this search.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {filterEligibleContractors(eligibleContractors, contractorSearch).map((c) => (
                      <li
                        key={c.contractor_id}
                        className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 sm:px-4"
                      >
                        <div className="min-w-0 text-left">
                          <div className="truncate font-medium text-gray-900">{c.user?.name || '—'}</div>
                          <div className="truncate text-xs text-gray-500">{c.user?.mobile || c.user?.email || ''}</div>
                        </div>
                        <button
                          type="button"
                          disabled={disabled || actionLoading === 'assign-contractor'}
                          onClick={() =>
                            runAction('assign-contractor', () =>
                              postAssignContractorToOrder(orderId, c.contractor_id)
                            )
                          }
                          className="shrink-0 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {actionLoading === 'assign-contractor' ? '…' : 'Assign'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : null}

          {hasAssignedWorker ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-sm text-emerald-950">
              <span className="font-medium">Worker assigned:</span>{' '}
              {orderWorker?.user?.name || '—'}
              {orderWorker?.user?.mobile ? ` · ${orderWorker.user.mobile}` : ''}
            </div>
          ) : null}

          {needsWorker ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Step 2 · Assign worker</h3>
              <p className="mt-1 text-xs text-gray-600">
                Workers on this contractor&apos;s team (same pattern as Step 1). Search, then tap Assign.
              </p>
              <input
                type="search"
                className={`${inputClass()} mt-3`}
                placeholder="Search by name, email, or mobile…"
                value={workerSearch}
                onChange={(e) => setWorkerSearch(e.target.value)}
                disabled={disabled}
              />
              <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                {loadingWorkers ? (
                  <p className="p-4 text-sm text-gray-500">Loading workers…</p>
                ) : contractorWorkers.length === 0 ? (
                  <p className="p-4 text-sm text-amber-800">
                    No workers returned for this contractor. Add workers on the contractor profile or check the API.
                  </p>
                ) : filterWorkersForSearch(contractorWorkers, workerSearch).length === 0 ? (
                  <p className="p-4 text-sm text-gray-500">No workers match this search.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {filterWorkersForSearch(contractorWorkers, workerSearch).map((w, idx) => {
                      const assignBody = buildAssignWorkerBody(w);
                      const rowKey =
                        assignBody.worker_id ||
                        assignBody.user_id ||
                        w.worker_id ||
                        w.user?.user_id ||
                        `worker-row-${idx}`;
                      const inactive = w.active === false;
                      const canAssign = Object.keys(assignBody).length > 0 && !inactive;
                      return (
                        <li
                          key={rowKey}
                          className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 sm:px-4 ${
                            inactive ? 'bg-gray-50/80' : ''
                          }`}
                        >
                          <div className="min-w-0 text-left">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="truncate font-medium text-gray-900">
                                {w.user?.name || '—'}
                              </span>
                              {inactive ? (
                                <span className="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                  Inactive
                                </span>
                              ) : null}
                            </div>
                            <div className="truncate text-xs text-gray-500">
                              {w.user?.mobile || w.user?.email || ''}
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={disabled || actionLoading === 'worker' || !canAssign}
                            title={inactive ? 'Activate this worker on the contractor profile to assign' : undefined}
                            onClick={() =>
                              canAssign
                                ? runAction('worker', () => postAssignWorker(orderId, assignBody))
                                : undefined
                            }
                            className="shrink-0 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            {actionLoading === 'worker' ? '…' : 'Assign'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <SubSection title={allowsQuotationSubmit ? 'Quotation & extra charges' : 'Extra charges'}>
        {allowsQuotationSubmit ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelClass()}>Quotation amount (dollars)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={inputClass()}
                  value={quotation.amount}
                  onChange={(e) => setQuotation((q) => ({ ...q, amount: e.target.value }))}
                  disabled={disabled}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass()}>Details</label>
                <textarea
                  className={inputClass()}
                  rows={2}
                  value={quotation.details}
                  onChange={(e) => setQuotation((q) => ({ ...q, details: e.target.value }))}
                  disabled={disabled}
                />
              </div>
              <OrderFlowImageUploadList
                label="Images"
                hint="Optional — upload photos to attach to the quotation."
                urls={quotation.images}
                onUrlsChange={(next) => setQuotation((q) => ({ ...q, images: next }))}
                disabled={disabled}
                busy={quotationImageBusy}
                onBusyChange={setQuotationImageBusy}
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={quotation.require_user_accept}
                  onChange={(e) =>
                    setQuotation((q) => ({ ...q, require_user_accept: e.target.checked }))
                  }
                  disabled={disabled}
                />
                Require user acceptance
              </label>
              <button
                type="button"
                disabled={disabled || actionLoading === 'quote' || quotation.amount === ''}
                onClick={() => {
                  const cents = dollarsStringToCents(quotation.amount);
                  if (Number.isNaN(cents) || cents < 0) {
                    toast.error('Enter a valid quotation amount in dollars (0 or more)');
                    return;
                  }
                  runAction(
                    'quote',
                    () =>
                      postQuotation(orderId, {
                        amount: cents,
                        details: quotation.details || undefined,
                        images: Array.isArray(quotation.images) ? quotation.images.filter(Boolean) : [],
                        require_user_accept: quotation.require_user_accept,
                      }),
                    loadFinancialsMaster
                  );
                }}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {actionLoading === 'quote' ? 'Saving…' : 'Submit quotation'}
              </button>
            </div>

            <hr className="border-gray-200" />
          </>
        ) : (
          <p className="text-sm text-gray-600">
            This service is priced by {serviceType === 'HOUR' ? 'hours' : 'quantity'}. Expected amount and{' '}
            {serviceType === 'HOUR' ? 'hours' : 'units'} are not edited in this panel — use your back office or order
            tools if you need to change them.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass()}>Charge type</label>
            <select
              className={inputClass()}
              value={extraCharge.charge_type}
              onChange={(e) => setExtraCharge((c) => ({ ...c, charge_type: e.target.value }))}
              disabled={disabled}
            >
              <option value="">Select…</option>
              {chargeTypeSelectOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {chargeTypesFromApi !== null && chargeTypesFromApi.length > 0 ? (
              <p className="mt-1 text-xs text-gray-500">Options come from your charge-type settings.</p>
            ) : null}
            {chargeTypeSelectOptions.length === 0 ? (
              <div className="mt-2">
                <label className={labelClass()}>Custom charge type</label>
                <input
                  className={inputClass()}
                  value={extraChargeTypeCustom}
                  onChange={(e) => setExtraChargeTypeCustom(e.target.value)}
                  placeholder="CHARGE_TYPE enum"
                  disabled={disabled}
                />
              </div>
            ) : null}
          </div>
          <div>
            <label className={labelClass()}>Amount (dollars)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className={inputClass()}
              value={extraCharge.amount}
              onChange={(e) => setExtraCharge((c) => ({ ...c, amount: e.target.value }))}
              disabled={disabled}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass()}>Remark</label>
            <input
              className={inputClass()}
              value={extraCharge.remark}
              onChange={(e) => setExtraCharge((c) => ({ ...c, remark: e.target.value }))}
              disabled={disabled}
            />
          </div>
          <OrderFlowImageUploadList
            label="Images"
            hint="Optional — upload photos for this extra charge."
            urls={extraCharge.images}
            onUrlsChange={(next) => setExtraCharge((c) => ({ ...c, images: next }))}
            disabled={disabled}
            busy={extraChargeImageBusy}
            onBusyChange={setExtraChargeImageBusy}
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={extraCharge.require_user_accept}
              onChange={(e) =>
                setExtraCharge((c) => ({ ...c, require_user_accept: e.target.checked }))
              }
              disabled={disabled}
            />
            Require user acceptance for extra charge
          </label>
          <button
            type="button"
            disabled={
              disabled ||
              actionLoading === 'xc' ||
              !(extraCharge.charge_type || extraChargeTypeCustom.trim()) ||
              extraCharge.amount === ''
            }
            onClick={() => {
              const cents = dollarsStringToCents(extraCharge.amount);
              if (Number.isNaN(cents) || cents < 0) {
                toast.error('Enter a valid charge amount in dollars');
                return;
              }
              runAction(
                'xc',
                () =>
                  postExtraCharge(orderId, {
                    charge_type: extraCharge.charge_type || extraChargeTypeCustom.trim(),
                    amount: cents,
                    remark: extraCharge.remark || undefined,
                    images: Array.isArray(extraCharge.images) ? extraCharge.images.filter(Boolean) : [],
                    require_user_accept: extraCharge.require_user_accept,
                  }),
                loadFinancialsMaster
              );
            }}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {actionLoading === 'xc' ? 'Adding…' : 'Add extra charge'}
          </button>
        </div>
      </SubSection>

      {showInspectionSection ? (
        <SubSection title="Order session flow">
          <AdminSessionFlowPanel
            orderId={orderId}
            orderStatus={orderStatus}
            orderWorker={orderWorker}
            orderContractorId={orderContractorId}
            disabled={disabled}
            sessions={sessionFlowSessions}
            flowLoading={sessionsBundleLoading}
            onRefreshAll={refreshAllOrderFlowAfterMutation}
            orderTimeOptions={orderTimeOptions}
            serviceType={serviceType}
            orderQuotation={orderQuotation}
          />
        </SubSection>
      ) : null}

      <SubSection title="Job lifecycle (step by step)">
        <JobLifecyclePanel
          orderStatus={orderStatus}
          orderContractorId={orderContractorId}
          hasAssignedWorker={hasAssignedWorker}
          disabled={disabled}
          actionLoading={actionLoading}
          endJobMinutes={endJobMinutes}
          setEndJobMinutes={setEndJobMinutes}
          orderId={orderId}
          runAction={runAction}
          useInspectionSessionFlow={sessionFlowReplacesLegacy}
          serviceType={service?.service_type}
          quotation={orderQuotation}
          loadFinancialsMaster={loadFinancialsMaster}
        />
      </SubSection>

      <SubSection title="Financials & Stripe">
        <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-left">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Invoice preview</h4>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={disabled || financialsMasterLoading}
                onClick={() => loadFinancialsMaster()}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              >
                {financialsMasterLoading ? 'Refreshing…' : 'Refresh'}
              </button>
              {!disabled ? (
                <button
                  type="button"
                  disabled={invoicePdfBusy}
                  onClick={handleInvoicePdf}
                  className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {invoicePdfBusy ? 'PDF…' : 'Download invoice PDF'}
                </button>
              ) : null}
            </div>
          </div>
          {financialsMasterError ? (
            <p className="text-sm text-red-700">{financialsMasterError}</p>
          ) : null}
          {moneyHint ? <p className="text-xs text-gray-600">{moneyHint}</p> : null}
          {financialsMasterLoading && !financialsMaster ? (
            <p className="text-sm text-gray-500">Loading invoice preview…</p>
          ) : null}
          {financialsMaster ? (
            <>
              {payCmp &&
              (typeof payCmp.amount_total_cents === 'number' ||
                typeof payCmp.amount_received_cents === 'number' ||
                typeof payCmp.amount_remaining_cents === 'number') ? (
                <div className="grid gap-3 rounded-lg border border-sky-200 bg-sky-50/80 p-3 sm:grid-cols-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-900/80">
                      Invoice
                    </p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">
                      {formatCentsAsCurrency(
                        invoiceMoneyCents(
                          payCmp.amount_total_cents ?? payCmp.invoice_total_cents ?? totalDue
                        ),
                        invoiceCurrency
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-900/80">
                      Received
                    </p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums text-emerald-800">
                      {formatCentsAsCurrency(
                        invoiceMoneyCents(payCmp.amount_received_cents ?? 0),
                        invoiceCurrency
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-900/80">
                      Remaining
                    </p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums text-amber-900">
                      {formatCentsAsCurrency(
                        invoiceMoneyCents(payCmp.amount_remaining_cents ?? 0),
                        invoiceCurrency
                      )}
                    </p>
                  </div>
                  {payCmp.note ? (
                    <p className="sm:col-span-3 text-xs text-gray-600">{payCmp.note}</p>
                  ) : null}
                </div>
              ) : totalDue != null ? (
                <p className="text-sm text-gray-800">
                  <span className="font-medium">Amount due (invoice total):</span>{' '}
                  <span className="text-lg font-semibold text-gray-900">
                    {formatCentsAsCurrency(invoiceMoneyCents(totalDue), invoiceCurrency)}
                  </span>
                </p>
              ) : null}
              {!disabled && amountRemainingCents != null && amountRemainingCents > 0 ? (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-indigo-950">Customer payment link</p>
                      <p className="mt-1 text-xs text-indigo-900/80">
                        Customer still owes{' '}
                        {formatCentsAsCurrency(amountRemainingCents, invoiceCurrency)}. Queues an SMS
                        with the pay link (created and sent by the worker).
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={paymentLinkSmsBusy}
                      onClick={handleSendPaymentLinkSms}
                      className="shrink-0 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {paymentLinkSmsBusy ? 'Sending…' : 'Send payment link by SMS'}
                    </button>
                  </div>
                </div>
              ) : null}
              {paymentRows.length > 0 ? (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Stripe payments
                  </p>
                  <ul className="space-y-2">
                    {paymentRows.map((row, idx) => {
                      const pid = paymentRowId(row) || `payment-${idx}`;
                      const st = paymentRowStatus(row);
                      return (
                        <li
                          key={pid}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-mono text-xs text-gray-700">{pid}</span>
                            <span
                              className={`rounded px-2 py-0.5 text-xs font-medium ${
                                st === 'SUCCEEDED'
                                  ? 'bg-green-100 text-green-800'
                                  : st === 'CANCELED'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {st || '—'}
                            </span>
                          </div>
                          <p className="mt-1 tabular-nums text-gray-900">
                            {formatCentsAsCurrency(paymentRowAmountCents(row), invoiceCurrency)}
                          </p>
                          <PaymentRefundRow
                            row={row}
                            orderCurrency={invoiceCurrency}
                            disabled={disabled}
                            onRefunded={loadFinancialsMaster}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
              {pendingPreTax != null ? (
                <p className="text-xs text-amber-900">
                  Waiting on customer approval:{' '}
                  {formatCentsAsCurrency(invoiceMoneyCents(pendingPreTax), invoiceCurrency)} (not added to the total until
                  they accept).
                </p>
              ) : null}
              {payCmp &&
              typeof payCmp.shortfall_cents === 'number' &&
              payCmp.shortfall_cents > 0 ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                  <p className="font-medium">Stripe hold is below invoice total</p>
                  <p className="mt-1 text-xs">
                    Invoice {formatCentsAsCurrency(invoiceMoneyCents(payCmp.invoice_total_cents), invoiceCurrency)} ·
                    Card hold {formatCentsAsCurrency(invoiceMoneyCents(payCmp.authorized_cents), invoiceCurrency)} · Still
                    needed {formatCentsAsCurrency(invoiceMoneyCents(payCmp.shortfall_cents), invoiceCurrency)}. Adjust the
                    PaymentIntent in Stripe if needed.
                  </p>
                </div>
              ) : null}
              <div className="rounded-lg border border-violet-200 bg-violet-50/90 p-3">
                <p className="text-xs font-semibold text-gray-900">2do Guarantee Fee</p>
                <p className="mt-1 text-[11px] text-gray-600">
                  {guaranteeFeeApplied
                    ? 'The guarantee fee is currently applied on this order. Use the button below only if it should not be charged for this job.'
                    : 'The guarantee fee is currently not applied. Use the button below to add it back if it should be charged.'}
                </p>
                <button
                  type="button"
                  disabled={disabled || actionLoading === 'gfee'}
                  onClick={() =>
                    runAction(
                      'gfee',
                      () => patchApplyGuaranteeFee(orderId, { apply_guarantee_fee: !guaranteeFeeApplied }),
                      loadFinancialsMaster
                    )
                  }
                  className="mt-2 rounded-md bg-violet-700 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-800 disabled:opacity-50"
                >
                  {actionLoading === 'gfee'
                    ? 'Saving…'
                    : guaranteeFeeApplied
                      ? 'Remove 2do Guarantee Fee'
                      : 'Apply 2do Guarantee Fee'}
                </button>
              </div>
              {lineItems.length > 0 ? (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    What the customer is charged
                  </p>
                  <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-gray-200 bg-gray-50 text-xs text-gray-600">
                        <tr>
                          <th className="px-3 py-2">Type</th>
                          <th className="px-3 py-2">Description</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((row, idx) => (
                          <tr key={row.id ?? `${row.kind}-${idx}`} className="border-b border-gray-100">
                            <td className="px-3 py-2 font-mono text-xs text-gray-700">
                              {row.kind || '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-800">
                              {row.label || row.title || row.description || '—'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {formatCentsAsCurrency(
                                invoiceMoneyCents(row.amount_cents ?? row.amount ?? 0),
                                invoiceCurrency
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
              {extraChargesList.length > 0 ? (
                <div className="mt-4">
                  <p className="mb-2 text-sm font-semibold text-gray-900">Extra charges</p>
                  <p className="mb-2 text-xs text-gray-600">
                    Each add-on from the server is listed here with an <strong>Edit</strong> button.
                  </p>
                  <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
                    {extraChargesList.map((c, idx) => {
                      const cid = orderFlowChargeRowId(c);
                      const cents = invoiceMoneyCents(c.amount_cents ?? c.amount ?? 0);
                      const canEdit = !disabled && orderFlowChargeAmountEditable(c);
                      return (
                        <li
                          key={cid || idx}
                          className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 sm:flex-nowrap"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {humanizeChargeTypeLabel(c.charge_type || c.type)}
                            </p>
                            <p className="text-xs text-gray-500">Status: {c.status || '—'}</p>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            <span className="text-sm font-semibold tabular-nums text-gray-900">
                              {formatCentsAsCurrency(cents, invoiceCurrency)}
                            </span>
                            {canEdit ? (
                              <button
                                type="button"
                                className="rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
                                onClick={() => setFinancialsEditingChargeId(String(cid))}
                              >
                                Edit
                              </button>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
              {contractorLines.length > 0 ? (
                <details className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                  <summary className="cursor-pointer text-sm font-medium text-emerald-900">
                    Contractor wallet preview
                  </summary>
                  <ul className="mt-2 space-y-1 text-xs text-emerald-900">
                    {contractorLines.map((row, idx) => (
                      <li key={idx}>
                        {row.kind || 'line'}:{' '}
                        {row.label || row.description || '—'} ·{' '}
                        {formatCentsAsCurrency(
                          invoiceMoneyCents(row.amount_cents ?? row.amount ?? 0),
                          invoiceCurrency
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
              {financialsEditingChargeId ? (
                <div className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50/30 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-gray-800">Change this line</p>
                    <button
                      type="button"
                      className="text-xs text-gray-600 underline hover:text-gray-900"
                      onClick={() => setFinancialsEditingChargeId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                  {(() => {
                    const ch = allCharges.find(
                      (c) => String(orderFlowChargeRowId(c)) === String(financialsEditingChargeId)
                    );
                    if (!ch) {
                      return (
                        <p className="text-xs text-gray-500">
                          This line could not be loaded. Tap <strong>Refresh</strong> above and try again.
                        </p>
                      );
                    }
                    return (
                      <ChargeEditRow
                        charge={ch}
                        orderId={orderId}
                        orderCurrency={invoiceCurrency}
                        orderStatus={orderStatus}
                        disabled={disabled}
                        onSaved={handleChargeEditorSaved}
                      />
                    );
                  })()}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </SubSection>

      <SubSection title="Complete order">
        {financialsMasterLoading ? (
          <p className="text-sm text-gray-500">Loading completion rules from server…</p>
        ) : null}
        {financialsMasterError ? (
          <p className="text-sm text-red-700">
            Invoice preview failed — completion stays disabled until the preview loads successfully (refresh above).
          </p>
        ) : null}
        <div>
          <label className={labelClass()}>Worker remark</label>
          <input
            className={inputClass()}
            value={completeForm.worker_remark}
            onChange={(e) => setCompleteForm((c) => ({ ...c, worker_remark: e.target.value }))}
            disabled={disabled}
          />
        </div>
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm font-semibold text-gray-900">Card — Stripe capture</p>
          <p className="text-xs text-gray-600">
            Manual capture on the saved PaymentIntent (<code className="rounded bg-gray-100 px-1">stripe_capture</code>
            ).
          </p>
          {structuralStripeBlockers.length > 0 ? (
            <ul className="list-inside list-disc text-xs text-amber-900">
              {structuralStripeBlockers.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          ) : null}
          {stripeCaptureSkippedNote ? (
            <p className="text-xs text-emerald-800">
              Invoice is paid in full — completing will finalize the order without additional card capture.
            </p>
          ) : null}
          {orderStatusUpper !== 'DONE' ? (
            <p className="text-xs text-amber-900">The order must be DONE before it can be completed.</p>
          ) : null}
          <button
            type="button"
            disabled={disabled || actionLoading === 'complete' || !canStripeComplete}
            onClick={handleCompleteOrder}
            className="rounded-lg bg-green-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-800 disabled:opacity-50"
          >
            {actionLoading === 'complete' ? 'Completing…' : 'Complete order'}
          </button>
        </div>
      </SubSection>
      {offlinePaymentWarning ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="offline-payment-warning-title"
        >
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
            <h3 id="offline-payment-warning-title" className="text-lg font-semibold text-gray-900">
              Online payment could not be collected
            </h3>
            <p className="mt-2 text-sm text-gray-700">{warningReason}</p>
            <p className="mt-2 text-sm text-gray-700">
              Record the remaining amount offline, then click <strong>Complete order</strong> again.
            </p>

            <div className="mt-4 grid gap-3 rounded-lg border border-amber-200 bg-amber-50/80 p-3 sm:grid-cols-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/70">
                  Invoice total
                </p>
                <p className="mt-0.5 text-base font-semibold tabular-nums text-gray-900">
                  {amountTotalCents == null
                    ? '—'
                    : formatCentsAsCurrency(amountTotalCents, invoiceCurrency)}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/70">
                  Collected so far
                </p>
                <p className="mt-0.5 text-base font-semibold tabular-nums text-emerald-800">
                  {amountReceivedCents == null
                    ? '—'
                    : formatCentsAsCurrency(amountReceivedCents, invoiceCurrency)}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/70">
                  Still required
                </p>
                <p className="mt-0.5 text-base font-semibold tabular-nums text-amber-950">
                  {amountRemainingCents == null
                    ? '—'
                    : formatCentsAsCurrency(amountRemainingCents, invoiceCurrency)}
                </p>
              </div>
            </div>

            <dl className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <dt className="font-medium text-gray-700">Payment method</dt>
                <dd className="text-gray-900">{warningPaymentMethodLabel}</dd>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <dt className="font-medium text-gray-700">Card on file</dt>
                <dd className="text-gray-900">{warningCardLabel}</dd>
              </div>
              {offlinePaymentWarning.message ? (
                <div className="border-t border-gray-200 pt-2">
                  <dt className="font-medium text-gray-700">Server message</dt>
                  <dd className="mt-1 text-xs text-gray-600">{offlinePaymentWarning.message}</dd>
                </div>
              ) : null}
            </dl>

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setOfflinePaymentWarning(null);
                  if (onRecordOfflinePayment) onRecordOfflinePayment();
                }}
                className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800"
              >
                Record offline payment
              </button>
              <button
                type="button"
                onClick={() => setOfflinePaymentWarning(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
}

export default AdminOrderFlowActions;
